<template>
  <div class="row">
    <q-card class="col-12 col-md-6 bg-grey-2" :class="{ 'bg-grey-10': $q.dark.isActive }" flat>
      <q-card-section class="q-py-xs"></q-card-section>
      <q-card-section>
        <div class="flex justify-between">
          <div class="text-h5 text-weight-medium q-my-auto">Subscribe to {{ planName }}</div>
        </div>
      </q-card-section>
      <q-card-section>
        <slot></slot>
        <q-separator />
        <div class="row q-my-md text-weight-medium">
          <div class="col">Subtotal</div>
          <div class="col justify-end">{{ formatMoney(price, 2) }}</div>
        </div>
        <q-input
          v-if="!addOn"
          v-model="promoCode"
          :error="promoCodeError"
          no-error-icon
          error-message="This code is invalid."
          filled
          placeholder="Add promotion code"
          class="q-my-md"
        />
        <q-separator />
        <div v-if="selectedCouponText" class="row q-my-md">
          <div class="col">Discount</div>
          <div class="col justify-end">{{ selectedCouponText }}</div>
        </div>
        <div v-if="trialDays" class="row q-my-md">
          <div class="col">Total after trial</div>
          <div class="col justify-end">{{ dueAfterPromo }}</div>
        </div>
        <div class="row text-subtitle1 text-weight-medium q-my-md">
          <div class="col">Total due today</div>
          <div v-if="trialDays" class="col justify-end">$0.00</div>
          <div v-else class="col justify-end">{{ dueAfterPromo }}</div>
        </div>
      </q-card-section>
    </q-card>
    <q-card class="col-12 col-md-6" flat bordered>
      <q-card-section class="bg-primary q-pa-xs"></q-card-section>
      <q-card-section>
        <div v-if="addOn" class="text-h6">Activate Add-On</div>
        <div v-else class="text-h6">Enter payment details</div>
      </q-card-section>
      <q-card-section v-if="!addOn">
        <div style="font-size: 0.93rem">Card Information</div>
        <div id="card-element" ref="card-element" :class="{ 'bg-grey-10': $q.dark.isActive }"></div>
        <div id="card-errors" class="text-red text-caption q-pt-sm" role="alert"></div>
        <div id="address-element" ref="address-element" class="q-pt-sm"></div>
      </q-card-section>
      <q-card-section v-if="isPrimaryOwner" class="row q-py-xs">
        <q-checkbox v-model="agreeToTerms" class="q-mr-sm"></q-checkbox>
        <div class="q-my-auto">
          I agree to HelloData's
          <a target="_blank" class="text-primary" href="https://www.hellodata.ai/terms/"
            >Terms of Service</a
          >
          and
          <a target="_blank" class="text-primary" href="https://www.hellodata.ai/privacy-policy/"
            >Privacy Policy</a
          >.
        </div>
      </q-card-section>
      <q-card-section>
        <template v-if="isPrimaryOwner">
          <q-btn
            color="primary"
            :disabled="!isReady"
            :loading="processing"
            class="full-width text-bold"
            size="large"
            no-caps
            unelevated
            @click="subscribe"
          >
            <q-spinner v-if="processing" size="24px" color="white"></q-spinner>
            <span v-else-if="trialDays">Start Trial</span>
            <span v-else-if="addOn">Activate Add-On</span>
            <span v-else>Subscribe</span>
          </q-btn>
          <p v-if="trialDays" class="text-grey-7 q-pt-lg">
            After your trial ends, you will be charged {{ dueAfterPromo }} (+ applicable taxes) per
            month starting {{ trialEndDate }}. You can always cancel before then.
          </p>
          <p v-if="addOn" class="text-grey-7 q-pt-lg">
            This add-on will be added to your existing Property Reports subscription. A prorated
            charge will be applied immediately, followed by the full amount on your next billing
            date.
          </p>
        </template>
        <div v-else>
          Please reach out to your primary account owner,
          <b>{{
            organizationMembers.find((_) => _.id === organization?.owner_user_id)?.email ||
            'Unknown'
          }}</b
          >, to make this purchase.
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script lang="ts">
import stripeAPI from '@/api/stripe'
import { formatMoney } from '@/utils'
import { format, addDays } from 'date-fns'
import {
  loadStripe,
  type Stripe,
  type StripeElements,
  type StripeCardElement,
  type StripeAddressElement
} from '@stripe/stripe-js'
import type { BillingAddress, OrganizationAttributes } from 'common'
import { useUserStore } from '@/stores/user'

export default {
  name: 'CheckoutCard',
  props: {
    planName: {
      type: String,
      required: true
    },
    price: {
      type: Number,
      required: true
    },
    trialDays: {
      type: Number,
      default: 0
    },
    addOn: {
      type: Boolean,
      default: false
    }
  },
  emits: ['create-subscription'],
  data() {
    return {
      stripe: null as Stripe | null,
      formatMoney,
      promoCodeError: false,
      agreeToTerms: false,
      cardHolderName: '',
      address: {
        line1: '',
        line2: undefined,
        city: '',
        state: '',
        postal_code: '',
        country: 'US'
      } as BillingAddress,
      cardElements: null as null | StripeElements,
      cardElementInputs: [] as (StripeCardElement | StripeAddressElement)[],
      processing: false,
      promoCode: '',
      promoCodeTimeout: undefined as undefined | ReturnType<typeof setTimeout>,
      selectedCoupon: null as null | {
        amount_off: number | null
        percent_off: number | null
        id: string
        active: boolean
      },
      isCardValid: false,
      isAddressValid: false
    }
  },
  computed: {
    stripeVariables() {
      const isDark = this.$q.dark.isActive
      return {
        colorText: isDark ? 'white' : 'black',
        colorBackground: isDark ? '#212121' : undefined,
        borderRadius: '4px',
        focusBoxShadow: 'none',
        fontSizeBase: '1em',
        colorPrimary: '#6365f1'
      }
    },
    isPrimaryOwner() {
      // is the actual owner of the organization in the DB
      return this.$auth0.user.value?.sub === this.organization?.owner_user_id
    },
    organization(): OrganizationAttributes | null {
      return useUserStore().organization
    },
    organizationMembers() {
      return useUserStore().organizationMembers
    },
    trialEndDate() {
      const endDay = addDays(new Date(), this.trialDays)
      return format(endDay, 'MMMM d, y')
    },
    dueAfterPromo() {
      let dueToday = this.price
      const percentOff = (this.selectedCoupon?.percent_off || 0) / 100
      const amountOff = (this.selectedCoupon?.amount_off || 0) / 100
      if (percentOff) {
        dueToday = dueToday * (1 - percentOff)
      }
      if (amountOff) {
        dueToday = dueToday - amountOff
      }
      return formatMoney(dueToday, 2)
    },
    selectedCouponText() {
      const percentOff = this.selectedCoupon?.percent_off
      const amountOff = (this.selectedCoupon?.amount_off || 0) / 100

      if (percentOff) {
        return `${percentOff}% off`
      } else if (amountOff) {
        return `${formatMoney(amountOff)} off`
      }
      return ''
    },
    isReady() {
      return (
        (this.addOn && this.agreeToTerms) ||
        (this.isCardValid && this.isAddressValid && this.agreeToTerms)
      )
    }
  },
  watch: {
    promoCode() {
      clearTimeout(this.promoCodeTimeout)
      this.promoCodeTimeout = setTimeout(() => {
        this.checkPromoCode()
      }, 500)
    },
    '$q.dark.isActive'() {
      this.cardElements?.update({ appearance: { variables: this.stripeVariables } })
      this.cardElementInputs.forEach((el) => {
        el.update({ style: { base: { color: this.stripeVariables.colorText } } })
      })
    }
  },
  async mounted() {
    const stripe = await this.getStripe()
    if (stripe && !this.addOn) {
      const elements = stripe.elements({
        appearance: { variables: this.stripeVariables }
      })
      const cardNumberElement = elements.create('card', {
        hidePostalCode: true,
        style: { base: { color: this.stripeVariables.colorText } }
      })
      cardNumberElement.on('change', (event) => {
        this.isCardValid = event.complete
        const displayError = document.getElementById('card-errors')
        if (displayError) {
          if (event.error) {
            displayError.textContent = event.error.message
          } else {
            displayError.textContent = ''
          }
        }
      })
      cardNumberElement.mount('#card-element')
      const addressElement = elements.create('address', {
        mode: 'billing',
        autocomplete: {
          mode: 'google_maps_api',
          apiKey: import.meta.env.VITE_GOOGLE_API_KEY
        },
        allowedCountries: ['US']
      })
      addressElement.on('change', (event) => {
        this.isAddressValid = event.complete
        this.address.line1 = event.value.address.line1
        this.address.line2 = event.value.address.line2 || undefined
        this.address.city = event.value.address.city
        this.address.state = event.value.address.state
        this.address.postal_code = event.value.address.postal_code
        this.cardHolderName = event.value.name
      })
      addressElement.mount('#address-element')
      this.cardElementInputs.push(cardNumberElement, addressElement)
      this.cardElements = elements
    }
  },
  methods: {
    async getStripe(): Promise<Stripe> {
      if (!this.stripe) {
        this.stripe = await loadStripe(import.meta.env.VITE_STRIPE_PRODUCT_ID).catch(() => {
          return null
        })
      }
      if (!this.stripe) {
        return await this.getStripe()
      }
      return this.stripe
    },
    async checkPromoCode() {
      if (!this.promoCode) {
        this.promoCodeError = false
        this.selectedCoupon = null
        return
      }
      const data = await stripeAPI.checkPromoCode(this.promoCode)
      if (data && data.active) {
        this.selectedCoupon = data
        this.promoCodeError = false
      } else {
        this.promoCodeError = true
        this.selectedCoupon = null
      }
    },
    async subscribe() {
      let paymentMethodId = null
      this.processing = true
      if (!this.addOn) {
        const stripe = await this.getStripe()
        if (!this.cardElements) {
          this.processing = false
          return
        }
        const cardElements = this.cardElements.getElement('card')
        if (!cardElements) {
          this.processing = false
          return
        }
        const { paymentMethod, error } = await stripe.createPaymentMethod({
          type: 'card',
          billing_details: {
            name: this.cardHolderName,
            address: {
              postal_code: this.address.postal_code
            }
          },
          card: cardElements
        })
        if (!paymentMethod || error) {
          this.processing = false
          return
        }
        if (this.selectedCoupon) {
          this.$tracker.usedCoupon(this.selectedCoupon.id)
        }
        paymentMethodId = paymentMethod.id
      }
      this.$emit('create-subscription', {
        paymentId: paymentMethodId,
        couponId: this.selectedCoupon?.id,
        address: this.address,
        cb: async () => {
          let path = ''
          if (this.$route.name === 'Subscribe to Property Reports') {
            path = '/property-reports'
          } else if (this.$route.name === 'Subscribe to Regional Reports') {
            path = '/regional-reports'
          }
          await this.$router.push({ name: 'Thank You', query: { path } })
          this.processing = false
        },
        failCb: () => {
          const displayError = document.getElementById('card-errors')
          if (displayError) {
            displayError.textContent =
              'An error occurred. Please confirm payment details then try again.'
          }
          this.processing = false
        }
      })
    }
  }
}
</script>
<style scoped lang="scss">
#card-form {
  display: flex;
  justify-content: space-around;
}

#card-element {
  border-radius: 4px;
  padding: 0.8rem;
  border: 1px solid rgba(50, 50, 93, 0.1);
  box-shadow:
    0px 1px 1px rgba(0, 0, 0, 0.03),
    0px 3px 6px rgba(0, 0, 0, 0.02);
}

.body--dark {
  #card-element {
    border: 1px solid #424242;
  }
}
</style>
